import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const IdGenerationPanel = ({ onGenerate, isGenerating }) => {
  const [selectedFormat, setSelectedFormat] = useState('standard');
  const [includeChecksum, setIncludeChecksum] = useState(true);
  const [notes, setNotes] = useState('');

  const formats = [
    {
      id: 'standard',
      name: 'Standard-Format',
      description: 'BA-YYYY-NNNNNN (z.B. BA-2025-123456)',
      example: 'BA-2025-123456',
      icon: 'FileText'
    },
    {
      id: 'extended',
      name: 'Erweitertes Format',
      description: 'BA-RR-YYYY-NNNNNN-CC (z.B. BA-01-2025-123456-A7)',
      example: 'BA-01-2025-123456-A7',
      icon: 'FileCode'
    },
    {
      id: 'legacy',
      name: 'Legacy-Format',
      description: 'BAYYYYNNNNNN (z.B. BA2025123456)',
      example: 'BA2025123456',
      icon: 'Archive'
    }
  ];

  const handleGenerate = () => {
    onGenerate({
      format: selectedFormat,
      includeChecksum,
      notes
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">ID-Generierung</h3>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-md text-sm font-medium">
          <Icon name="Fingerprint" size={16} />
          <span>Eindeutige ID</span>
        </div>
      </div>
      <div className="space-y-6">
        {/* Format Selection */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">ID-Format auswählen</label>
          <div className="space-y-3">
            {formats?.map((format) => (
              <div
                key={format?.id}
                onClick={() => setSelectedFormat(format?.id)}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedFormat === format?.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    selectedFormat === format?.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    <Icon name={format?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-foreground">{format?.name}</h4>
                      {selectedFormat === format?.id && (
                        <Icon name="CheckCircle" size={16} color="var(--color-primary)" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{format?.description}</p>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-muted rounded text-xs font-mono text-foreground">
                      <Icon name="Hash" size={12} />
                      <span>{format?.example}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Options */}
        <div className="pt-4 border-t border-border">
          <label className="block text-sm font-medium text-foreground mb-3">Generierungsoptionen</label>
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeChecksum}
                onChange={(e) => setIncludeChecksum(e?.target?.checked)}
                className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Prüfsumme einschließen</span>
                <p className="text-xs text-muted-foreground">Fügt eine Validierungsprüfsumme zur ID hinzu</p>
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                defaultChecked={true}
                className="w-5 h-5 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
              />
              <div>
                <span className="text-sm font-medium text-foreground">Automatische Datenbanksynchronisation</span>
                <p className="text-xs text-muted-foreground">Synchronisiert die ID sofort mit dem Hauptsystem</p>
              </div>
            </label>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">Begründung (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e?.target?.value)}
            placeholder="Geben Sie eine Begründung für die ID-Generierung ein..."
            rows={3}
            className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm text-foreground bg-background"
          />
          <p className="text-xs text-muted-foreground mt-2">Diese Notiz wird im Audit-Trail gespeichert</p>
        </div>

        {/* Generation Info */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-foreground mb-1">Generierungsinformationen</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Die ID wird nach Bundesstandards generiert</li>
                <li>• Automatische Duplikatsprüfung wird durchgeführt</li>
                <li>• Alle Aktionen werden im Audit-Trail protokolliert</li>
                <li>• Die Generierung dauert in der Regel weniger als 2 Sekunden</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader2" size={20} className="animate-spin" />
              <span>ID wird generiert...</span>
            </>
          ) : (
            <>
              <Icon name="Zap" size={20} />
              <span>Eindeutige ID generieren</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default IdGenerationPanel;